import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Plus, Loader2, Trash2, Pencil, UsersRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  admin: "Admin",
  doctor: "Doctor",
  receptionist: "Receptionist",
  super_admin: "Super Admin",
};

const ROLE_COLORS = {
  admin: "bg-violet-50 text-violet-700 border-violet-200",
  doctor: "bg-blue-50 text-blue-700 border-blue-200",
  receptionist: "bg-emerald-50 text-emerald-700 border-emerald-200",
  super_admin: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/members");
      setMembers(data);
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRemove = async (m) => {
    if (m.user_id === user?.user_id) {
      toast.error("You cannot remove yourself");
      return;
    }
    if (!confirm(`Remove ${m.name} from this clinic?`)) return;
    try {
      await api.delete(`/team/members/${m.user_id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto fade-in" data-testid="team-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">
            People
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Team members
          </h1>
          <p className="text-slate-600 mt-1">Manage admin, doctor, and receptionist accounts in your clinic.</p>
        </div>
        <Button onClick={() => { setEditTarget(null); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-team-member-btn">
          <Plus className="w-4 h-4 mr-2" /> Add team member
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
          ) : members.length === 0 ? (
            <EmptyState
              onAction={() => { setEditTarget(null); setDialogOpen(true); }}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => {
                  const isSelf = m.user_id === user?.user_id;
                  return (
                    <TableRow key={m.user_id} data-testid={`team-row-${m.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-sky-400 text-white flex items-center justify-center text-sm font-semibold">
                            {m.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">{m.name}</div>
                            {isSelf && <div className="text-xs text-blue-600 mt-0.5">You</div>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{m.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={ROLE_COLORS[m.role] || "bg-slate-50 text-slate-700 border-slate-200"}>
                          {ROLE_LABELS[m.role] || m.role}
                        </Badge>
                      </TableCell>
                      <TableCell><span className="text-xs capitalize">{m.provider}</span></TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {new Date(m.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditTarget(m); setDialogOpen(true); }} data-testid={`edit-team-${m.user_id}`} disabled={m.role === "super_admin"}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 ml-1" onClick={() => handleRemove(m)} disabled={isSelf || m.role === "super_admin"} data-testid={`remove-team-${m.user_id}`}>
                          <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TeamMemberDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editTarget}
        onSaved={load}
      />
    </div>
  );
}

function EmptyState({ onAction }) {
  return (
    <div className="p-16 text-center" data-testid="team-empty-state">
      <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
        <UsersRound className="w-7 h-7 text-blue-600" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>
        No team members yet
      </h3>
      <p className="text-sm text-slate-500 mb-5 max-w-sm mx-auto">
        Add doctors, receptionists, and admins to your clinic so they can sign in with their own account.
      </p>
      <Button onClick={onAction} className="bg-blue-600 hover:bg-blue-700">
        <Plus className="w-4 h-4 mr-2" /> Add first team member
      </Button>
    </div>
  );
}

function TeamMemberDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "doctor" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) setForm({ name: initial.name || "", email: initial.email || "", password: "", role: initial.role || "doctor" });
      else setForm({ name: "", email: "", password: "", role: "doctor" });
      setError("");
    }
  }, [open, initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (isEdit) {
        await api.put(`/team/members/${initial.user_id}`, { name: form.name, role: form.role });
        toast.success("Member updated");
      } else {
        await api.post("/team/members", form);
        toast.success("Member added");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            {isEdit ? "Edit team member" : "Add team member"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Update name or role. Email cannot be changed." : "Invite a new admin, doctor, or receptionist to your clinic."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="team-name-input" className="mt-1.5 h-10" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} disabled={isEdit} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="team-email-input" className="mt-1.5 h-10" />
          </div>
          {!isEdit && (
            <div>
              <Label>Temporary password</Label>
              <Input type="text" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder="Share securely with the team member" data-testid="team-password-input" className="mt-1.5 h-10" />
              <p className="text-xs text-slate-400 mt-1">Min. 6 characters. They can change it later via Forgot Password.</p>
            </div>
          )}
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="team-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin · Full access</SelectItem>
                <SelectItem value="doctor">Doctor · Clinical + billing</SelectItem>
                <SelectItem value="receptionist">Receptionist · Patients & appointments</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="team-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save" : "Add member")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
